import React, { useState } from 'react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { ChevronLeftIcon, ChevronRightIcon, FilterIcon, CalendarIcon, ListIcon, PlusIcon, XIcon, CheckIcon } from 'lucide-react';
import { CalendarView } from './CalendarView';
import { FilterBar } from './FilterBar';
import { TrainingDetailsModal } from './TrainingDetailsModal';
import { Badge } from '../ui/Badge';
// Mock data for slipbaan locations
const mockLocations = [{
  id: 1,
  name: 'Rotterdam',
  address: 'Havenstraat 123',
  capacity: 3
}, {
  id: 2,
  name: 'Utrecht',
  address: 'Stationsweg 45',
  capacity: 2
}, {
  id: 3,
  name: 'Amsterdam',
  address: 'Industrieweg 78',
  capacity: 2
}, {
  id: 4,
  name: 'Eindhoven',
  address: 'Techniekplein 12',
  capacity: 1
}];
// Mock data for training types
const mockTrainingTypes = [{
  id: 1,
  name: 'Sliptraining Basis',
  color: 'blue',
  duration: 180
}, {
  id: 2,
  name: 'Defensief Rijden',
  color: 'green',
  duration: 240
}, {
  id: 3,
  name: 'Sliptraining Gevorderd',
  color: 'purple',
  duration: 210
}, {
  id: 4,
  name: 'Code 95 Training',
  color: 'yellow',
  duration: 300
}, {
  id: 5,
  name: 'Calamiteitentraining',
  color: 'red',
  duration: 180
}];
// Mock data for instructors
const mockInstructors = [{
  id: 1,
  name: 'Erik de Vries',
  availability: [1, 2, 3, 4, 5],
  specialties: [1, 3, 5]
}, {
  id: 2,
  name: 'Lisa Bakker',
  availability: [1, 3, 5],
  specialties: [2, 4]
}, {
  id: 3,
  name: 'Peter Jansen',
  availability: [2, 4],
  specialties: [1, 2, 3, 4, 5]
}, {
  id: 4,
  name: 'Maria Visser',
  availability: [1, 2, 3, 4, 5],
  specialties: [1, 2]
}, {
  id: 5,
  name: 'Thomas Berg',
  availability: [3, 4, 5],
  specialties: [3, 5]
}];
// Mock data for training sessions
const generateMockTrainingSessions = () => {
  const today = new Date();
  const startOfWeek = new Date(today);
  startOfWeek.setDate(today.getDate() - today.getDay() + 1); // Start from Monday
  const sessions = [];
  // Generate random sessions for the next 2 weeks
  for (let day = 0; day < 14; day++) {
    const date = new Date(startOfWeek);
    date.setDate(startOfWeek.getDate() + day);
    // Skip weekends
    if (date.getDay() === 0 || date.getDay() === 6) continue;
    // Generate 2-5 sessions per day
    const sessionsCount = Math.floor(Math.random() * 4) + 2;
    for (let i = 0; i < sessionsCount; i++) {
      const locationId = mockLocations[Math.floor(Math.random() * mockLocations.length)].id;
      const trainingTypeId = mockTrainingTypes[Math.floor(Math.random() * mockTrainingTypes.length)].id;
      const trainingType = mockTrainingTypes.find(t => t.id === trainingTypeId);
      const instructorId = mockInstructors[Math.floor(Math.random() * mockInstructors.length)].id;
      // Generate random start time between 8:00 and 16:00
      const startHour = Math.floor(Math.random() * 8) + 8;
      const startMinute = Math.random() > 0.5 ? 30 : 0;
      const startTime = new Date(date);
      startTime.setHours(startHour, startMinute, 0, 0);
      const endTime = new Date(startTime);
      endTime.setMinutes(endTime.getMinutes() + trainingType.duration);
      sessions.push({
        id: sessions.length + 1,
        locationId,
        trainingTypeId,
        instructorId,
        startTime,
        endTime,
        participants: Math.floor(Math.random() * 8) + 3,
        notes: Math.random() > 0.7 ? 'Speciale aandacht voor parkeermanoeuvres' : ''
      });
    }
  }
  return sessions;
};
const mockTrainingSessions = generateMockTrainingSessions();
export function SlipbaanPlanner() {
  const [viewMode, setViewMode] = useState<'day' | 'week'>('week');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [showFilters, setShowFilters] = useState(false);
  const [selectedTrainingSession, setSelectedTrainingSession] = useState(null);
  const [filters, setFilters] = useState({
    locations: mockLocations.map(loc => loc.id),
    trainingTypes: mockTrainingTypes.map(type => type.id),
    dateRange: {
      start: new Date(),
      end: new Date(new Date().setDate(new Date().getDate() + 30))
    }
  });
  // Function to navigate to today
  const goToToday = () => {
    setCurrentDate(new Date());
  };
  // Function to navigate to previous day/week
  const goToPrevious = () => {
    const newDate = new Date(currentDate);
    if (viewMode === 'day') {
      newDate.setDate(newDate.getDate() - 1);
    } else {
      newDate.setDate(newDate.getDate() - 7);
    }
    setCurrentDate(newDate);
  };
  // Function to navigate to next day/week
  const goToNext = () => {
    const newDate = new Date(currentDate);
    if (viewMode === 'day') {
      newDate.setDate(newDate.getDate() + 1);
    } else {
      newDate.setDate(newDate.getDate() + 7);
    }
    setCurrentDate(newDate);
  };
  // Function to toggle filters
  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };
  // Function to apply filters
  const applyFilters = newFilters => {
    setFilters(newFilters);
    setShowFilters(false);
  };
  // Function to open training details modal
  const openTrainingDetails = trainingSession => {
    setSelectedTrainingSession(trainingSession);
  };
  // Function to close training details modal
  const closeTrainingDetails = () => {
    setSelectedTrainingSession(null);
  };
  // Filter training sessions based on current filters
  const filteredTrainingSessions = mockTrainingSessions.filter(session => {
    // Filter by location
    if (!filters.locations.includes(session.locationId)) return false;
    // Filter by training type
    if (!filters.trainingTypes.includes(session.trainingTypeId)) return false;
    // Filter by date range
    const sessionDate = new Date(session.startTime);
    const startDate = new Date(filters.dateRange.start);
    const endDate = new Date(filters.dateRange.end);
    startDate.setHours(0, 0, 0, 0);
    endDate.setHours(23, 59, 59, 999);
    if (sessionDate < startDate || sessionDate > endDate) return false;
    return true;
  });
  // Format date for header display
  const formatDateHeader = () => {
    if (viewMode === 'day') {
      return currentDate.toLocaleDateString('nl-NL', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
    } else {
      const startOfWeek = new Date(currentDate);
      startOfWeek.setDate(currentDate.getDate() - currentDate.getDay() + 1); // Start from Monday
      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(startOfWeek.getDate() + 4); // End on Friday
      return `${startOfWeek.toLocaleDateString('nl-NL', {
        day: 'numeric',
        month: 'long'
      })} - ${endOfWeek.toLocaleDateString('nl-NL', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      })}`;
    }
  };
  return <div className="space-y-4" data-id="element-3075">
      <div className="flex justify-between items-center" data-id="element-3076">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-3077">Slipbaanplanner</h1>
        <div className="flex space-x-2" data-id="element-3078">
          <Button variant="outline" size="sm" onClick={toggleFilters} data-id="element-3079">
            <FilterIcon className="h-4 w-4 mr-1" data-id="element-3080" />
            Filters {showFilters ? <XIcon className="h-3 w-3 ml-1" data-id="element-3081" /> : null}
          </Button>
          <Button variant="primary" size="sm" data-id="element-3082">
            <PlusIcon className="h-4 w-4 mr-1" data-id="element-3083" />
            Nieuwe Training
          </Button>
        </div>
      </div>
      {showFilters && <Card data-id="element-3084">
          <FilterBar locations={mockLocations} trainingTypes={mockTrainingTypes} currentFilters={filters} onApplyFilters={applyFilters} onCancel={() => setShowFilters(false)} data-id="element-3085" />
        </Card>}
      <Card className="overflow-hidden" data-id="element-3086">
        <div className="flex justify-between items-center p-4 border-b border-gray-200" data-id="element-3087">
          <div className="flex space-x-2" data-id="element-3088">
            <Button variant="outline" size="sm" onClick={goToPrevious} data-id="element-3089">
              <ChevronLeftIcon className="h-4 w-4" data-id="element-3090" />
            </Button>
            <Button variant="outline" size="sm" onClick={goToToday} data-id="element-3091">
              Vandaag
            </Button>
            <Button variant="outline" size="sm" onClick={goToNext} data-id="element-3092">
              <ChevronRightIcon className="h-4 w-4" data-id="element-3093" />
            </Button>
          </div>
          <h2 className="text-lg font-medium text-gray-900" data-id="element-3094">
            {formatDateHeader()}
          </h2>
          <div className="flex space-x-2" data-id="element-3095">
            <Button variant={viewMode === 'day' ? 'primary' : 'outline'} size="sm" onClick={() => setViewMode('day')} data-id="element-3096">
              <CalendarIcon className="h-4 w-4 mr-1" data-id="element-3097" />
              Dag
            </Button>
            <Button variant={viewMode === 'week' ? 'primary' : 'outline'} size="sm" onClick={() => setViewMode('week')} data-id="element-3098">
              <ListIcon className="h-4 w-4 mr-1" data-id="element-3099" />
              Week
            </Button>
          </div>
        </div>
        <div className="overflow-x-auto" data-id="element-3100">
          <CalendarView viewMode={viewMode} currentDate={currentDate} locations={mockLocations} trainingSessions={filteredTrainingSessions} trainingTypes={mockTrainingTypes} instructors={mockInstructors} onTrainingClick={openTrainingDetails} data-id="element-3101" />
        </div>
      </Card>
      {/* Active Filters Summary */}
      {(filters.locations.length < mockLocations.length || filters.trainingTypes.length < mockTrainingTypes.length) && <div className="flex items-center space-x-2 text-sm text-gray-600" data-id="element-3102">
          <span data-id="element-3103">Actieve filters:</span>
          {filters.locations.length < mockLocations.length && <Badge variant="info" data-id="element-3104">
              {filters.locations.length} van {mockLocations.length} locaties
            </Badge>}
          {filters.trainingTypes.length < mockTrainingTypes.length && <Badge variant="info" data-id="element-3105">
              {filters.trainingTypes.length} van {mockTrainingTypes.length}{' '}
              trainingstypes
            </Badge>}
          <Button variant="ghost" size="sm" onClick={() => setFilters({
        ...filters,
        locations: mockLocations.map(loc => loc.id),
        trainingTypes: mockTrainingTypes.map(type => type.id)
      })} data-id="element-3106">
            Reset filters
          </Button>
        </div>}
      {/* Training Details Modal */}
      {selectedTrainingSession && <TrainingDetailsModal trainingSession={selectedTrainingSession} locations={mockLocations} trainingTypes={mockTrainingTypes} instructors={mockInstructors} onClose={closeTrainingDetails} data-id="element-3107" />}
    </div>;
}